// 400 Instagram-style post concepts for BPC across 4 growth themes.
// Formats (navy/silver/white): "list" (numbered), "stat", "principle".
// This mirrors the server-side Pillow render (render400.py) 1:1 so the
// on-screen tiles match the downloadable PNGs.

const THEMES = {
  "Sustainable Growth": {
    ANG: ["The playbook:", "Field note:", "In practice:", "Growth lever:", "Do this:", "Principle:"],
    HEAD: [
      "Build growth that lasts", "Grow the core first", "Turn strategy into momentum", "Compound your best channels",
      "Win markets that matter", "Fund what actually works", "Make growth repeatable", "Balance ambition and discipline",
      "Scale what is proven", "Grow with purpose", "Protect long-term value", "Turn plans into results",
    ],
    PTS: [
      "Reinvest wins into the next quarter", "Cut the weakest 20% of spend", "Measure to revenue, not vanity",
      "Codify what worked last time", "Focus on one winnable segment", "Raise share of wallet first",
      "Pilot before you scale", "Kill or double down in 90 days", "Protect the compounding channels",
      "Align teams on one number", "Remove friction to buy again", "Sequence bets by value and speed",
    ],
    STAT: [
      ["2x", "Firms that unify brand and demand outgrow peers."],
      ["70%", "Most untapped growth sits inside today's base."],
      ["90", "The best bets prove out within 90 days."],
      ["80/20", "Growth hides in the 20% that compound."],
      ["5%", "A small retention lift can move profit a lot."],
      ["Q", "Reset priorities every quarter, not every year."],
      ["1", "Win one segment before you spread thin."],
      ["-20%", "Trim the weakest channels to fund the best."],
      ["+", "Loyal customers cost less and buy more."],
      ["3x", "Focused portfolios beat scattered bets."],
      ["1%", "A 1% price gain can beat a 1% cost cut."],
      ["wk", "Ship weekly, learn weekly, compound."],
      ["#1", "Focus is the strongest predictor of durable growth."],
      ["x", "Repeatable beats heroic every quarter."],
    ],
    PRIN: [
      "Growth is a system you build, not a sprint.", "Focus beats presence in every market.",
      "Repeatable beats heroic every quarter.", "Your best next customer is often a current one.",
      "Depth in one segment beats thin coverage.", "Reinvest in what compounds.",
      "Sustainable growth is disciplined growth.", "Win the core before the edges.",
      "Reset priorities every 90 days.", "Protect the channels that pay back for years.",
      "Fund on evidence, not opinion.", "Make expansion effortless.",
      "Grow with purpose, not just pace.", "Momentum compounds when you stay consistent.",
    ],
  },
  "AI & Execution": {
    ANG: ["The playbook:", "Field note:", "In practice:", "Execution lever:", "Do this:", "Principle:"],
    HEAD: [
      "Turn AI into real output", "Execute faster with AI", "Ship value, not demos", "Make AI part of the workflow",
      "Scale what the pilot proved", "Automate the repeatable", "Move from insight to action", "Build a high-performing team",
      "Cut cycle time with AI", "Keep humans in the loop", "Turn plans into weekly wins", "Operationalise your best ideas",
    ],
    PTS: [
      "Start with one high-volume workflow", "Keep a human in the loop", "Guardrail the brand voice first",
      "Measure lift, not novelty", "Automate the boring reports", "Give every decision one owner",
      "Remove one step from each stage", "Template the task, vary the proof", "Ship weekly and learn weekly",
      "Default to written pre-reads", "Scale only on evidence", "Fund learning, not just launches",
    ],
    STAT: [
      ["1", "Start AI with one high-volume workflow."],
      ["-1", "Remove one step from every stage."],
      ["3x", "AI-assisted teams execute faster."],
      ["wk", "Ship weekly; momentum compounds."],
      ["0", "Zero vanity metrics — report to outcomes."],
      ["1", "Every decision needs exactly one owner."],
      ["x", "Automation multiplies your best people."],
      ["Q", "Review adoption every quarter."],
      ["2", "Scale the two pilots that actually work."],
      ["min", "Cut hours to minutes on repeat tasks."],
      ["+", "Keep a human in the loop for trust."],
      ["%", "Measure lift, not novelty."],
      ["#1", "Execution, not ideas, is the real edge."],
      ["fast", "Speed comes from fewer, sharper decisions."],
    ],
    PRIN: [
      "Ideas are cheap; execution is the edge.", "Speed comes from fewer, sharper decisions.",
      "The goal of AI is a better decision.", "Automate the repeatable, elevate the human.",
      "Ship, then improve.", "Guardrail the brand before you scale AI.",
      "Measure lift, not novelty.", "Fund learning, not just launches.",
      "Give every decision one owner.", "Start small, prove value, then scale.",
      "Great advice is only as good as its execution.", "Keep a human in the loop.",
      "Consistency beats intensity.", "Turn insight into weekly action.",
    ],
  },
  "Innovation & Transformation": {
    ANG: ["The playbook:", "Field note:", "In practice:", "Transformation lever:", "Do this:", "Principle:"],
    HEAD: [
      "Turn ideas into fundable bets", "Transform without breaking trust", "Reinvent the core, not just the edge",
      "Make change stick", "Build a portfolio of bets", "Modernise the operating model", "Turn disruption into advantage",
      "Innovate where it pays", "Design for the next model", "Move from pilot to platform",
      "Change the system, not the symptom", "Future-proof the business",
    ],
    PTS: [
      "Run many small experiments", "Write the hypothesis first", "Set the kill criteria upfront",
      "Scale only what earns it", "Put decisions closest to data", "Remove hand-offs that slow you",
      "Rewire incentives to the new model", "Fund on evidence, not opinion", "Retire what no longer works",
      "Bring customers into the design", "Sequence change in 90-day waves", "Protect the core while you build",
    ],
    STAT: [
      ["many", "Run many small bets; scale the winners."],
      ["2", "Scale the two bets that earn it."],
      ["90", "Sequence transformation in 90-day waves."],
      ["1", "Change the system, not the symptom."],
      ["x", "Reinvention resets the clock — do it deliberately."],
      ["Q", "Review the innovation portfolio quarterly."],
      ["0", "Zero sacred cows when the model shifts."],
      ["+", "New models need new incentives."],
      ["#1", "Execution is where most transformations fail."],
      ["3", "Balance run, grow and transform bets."],
      ["kill", "Set kill criteria before you start."],
      ["evd", "Fund on evidence, not opinion."],
      ["wave", "Change in waves, not big bangs."],
      ["core", "Protect the core while you build the next."],
    ],
    PRIN: [
      "Innovation needs a portfolio, not a moonshot.", "Transformation is behaviour change, not a memo.",
      "The goal of an experiment is a decision.", "Reinvent the core, not just the edge.",
      "Change the system, not the symptom.", "Fund on evidence, not opinion.",
      "Protect the core while you build the next.", "Retire what no longer serves growth.",
      "Bring customers into the design.", "New models require new incentives.",
      "Small bets, fast learning, bold scaling.", "Sequence change in 90-day waves.",
      "Disruption is an opportunity you design for.", "Make change safe, then make it fast.",
    ],
  },
  "People & Culture": {
    ANG: ["The playbook:", "Field note:", "In practice:", "Culture lever:", "Do this:", "Principle:"],
    HEAD: [
      "Put people at the core of growth", "Build a culture that performs", "Grow people to grow the business",
      "Make excellence the default", "Lead change with your team", "Turn values into behaviours",
      "Build teams that own outcomes", "Create clarity, then trust", "Reward what you want repeated",
      "Develop leaders at every level", "Make it safe to speak up", "Hire for the culture you want",
    ],
    PTS: [
      "Reward outcomes, not activity", "Put decisions closest to the work", "Make expectations explicit",
      "Coach the moments that matter", "Celebrate learning, not just wins", "Remove blockers, not just tasks",
      "Hire for values and slope", "Give feedback early and kindly", "Protect focus time",
      "Model the behaviour you expect", "Share context, then trust", "Make recognition specific",
    ],
    STAT: [
      ["#1", "People are the core of every growth strategy."],
      ["1", "Clarity first, then trust and speed."],
      ["+", "Engaged teams outperform on every metric."],
      ["x", "Culture multiplies or divides strategy."],
      ["0", "Zero tolerance for unclear ownership."],
      ["wk", "Feedback works best early and often."],
      ["2", "Two things scale: clarity and trust."],
      ["Q", "Invest in people every quarter."],
      ["safe", "Psychological safety unlocks performance."],
      ["own", "Teams that own outcomes deliver them."],
      ["slope", "Hire for values and learning slope."],
      ["focus", "Protect focus; it compounds output."],
      ["lead", "Leaders set the standard by example."],
      ["care", "Care and candour are not opposites."],
    ],
    PRIN: [
      "People are the core of every growth strategy.", "Culture eats strategy without execution.",
      "Reward what you want repeated.", "Clarity first, then trust, then speed.",
      "Make it safe to speak up.", "Coach the moments that matter.",
      "Diverse teams, one standard: excellence.", "Leaders set the standard by example.",
      "Hire for values and slope.", "Recognition should be specific and timely.",
      "Protect focus; it compounds.", "Share context, then trust.",
      "Growth is a team sport.", "Care and candour make great teams.",
    ],
  },
  "Customer Experience": {
    ANG: ["The playbook:", "Field note:", "In practice:", "CX lever:", "Do this:", "Principle:"],
    HEAD: [
      "Design journeys, not touchpoints", "Turn service into loyalty", "Make every moment count",
      "Fix what customers feel", "Turn feedback into action", "Reduce effort at every step",
      "Personalise without the creep", "Recover better than you fail", "Make loyalty an engine",
      "Listen where buying starts", "Close the loop on every signal", "Turn support into growth",
    ],
    PTS: [
      "Map the end-to-end journey", "Fix the highest-friction moment", "Own the hand-offs between teams",
      "Reduce customer effort first", "Reward the behaviours you want", "Close the loop on every signal",
      "Segment by intent, not identity", "Recover fast when things break", "Measure the moments that matter",
      "Turn detractors into promoters", "Make help easy to find", "Act on feedback within a week",
    ],
    STAT: [
      ["14pts", "Journey redesign can lift retention double digits."],
      ["5%", "A small retention lift can move profit a lot."],
      ["2", "Fix the two journeys that drive most churn."],
      ["1", "Reduce effort — it predicts loyalty best."],
      ["+", "Loyal customers cost less and buy more."],
      ["wk", "Act on feedback within a week, not a quarter."],
      ["x", "Great recovery can beat a flawless first try."],
      ["0", "Zero silent failures — close every loop."],
      ["#1", "Effort is the strongest driver of loyalty."],
      ["Q", "Review the top journeys every quarter."],
      ["3x", "Promoters refer several times more often."],
      ["nps", "Track promoters, not just averages."],
      ["seam", "Customers feel the seams between teams."],
      ["care", "Care shows up in the small moments."],
    ],
    PRIN: [
      "Design journeys, not touchpoints.", "Customers feel the seams between your teams.",
      "Reduce effort before you add delight.", "Loyalty is an economic engine, not a metric.",
      "Recover better than you fail.", "Close the loop on every signal.",
      "The best growth signal is an unmet need.", "Reward the behaviours you want repeated.",
      "Listen where the buying really starts.", "Personalise at scale, but stay human.",
      "Fix the moment that quietly drives churn.", "Act on feedback fast or it decays.",
      "Every touch is a chance to earn trust.", "Service is marketing you can feel.",
    ],
  },
};

export const THEME_KEYS = Object.keys(THEMES);

function buildTheme(theme, tIndex) {
  const C = THEMES[theme];
  const posts = [];
  let localIdx = 0;
  // 72 list posts = 6 angles x 12 heads
  C.ANG.forEach((a) => {
    C.HEAD.forEach((h, hi) => {
      posts.push({
        type: "list",
        theme,
        tag: theme,
        title: `${a} ${h}`.trim(),
        sub: "How ambitious teams turn this into durable growth.",
        points: [C.PTS[hi % 12], C.PTS[(hi + 4) % 12], C.PTS[(hi + 8) % 12]],
      });
      localIdx += 1;
    });
  });
  C.STAT.forEach((s) => posts.push({ type: "stat", theme, tag: theme, stat: s[0], line: s[1] }));
  C.PRIN.forEach((pr) => posts.push({ type: "principle", theme, tag: theme, text: pr }));
  return posts.slice(0, 100).map((p, i) => ({ ...p, id: `T${tIndex + 1}-${i + 1}` }));
}

export function getInstagramStudioPosts() {
  const posts = [];
  THEME_KEYS.forEach((theme, tIndex) => {
    buildTheme(theme, tIndex).forEach((p) => posts.push(p));
  });
  return posts.map((p, i) => ({ ...p, num: i + 1 }));
}
